/**
 * Inline Node Config Registry
 *
 * Maps component types to their dedicated inline config components.
 * These are compact, NodeUI-based views purpose-built for on-canvas editing.
 *
 * Components not in this registry fall back to the existing drawer template
 * rendered via InlineConfigPanel's legacy path.
 */
import React from 'react';

// ─── Lazy-loaded inline config components ─────────────────────────────

// Core Actions
const TimeDelayInline = React.lazy(() => import('./TimeDelayInline'));
const EmailInline = React.lazy(() => import('./EmailInline'));
const SlackInline = React.lazy(() => import('./SlackInline'));
const CodeInline = React.lazy(() => import('./CodeInline'));
const WebhookInline = React.lazy(() => import('./WebhookInline'));
const WebhookTriggerInline = React.lazy(() => import('./WebhookTriggerInline'));
const ApiExportInline = React.lazy(() => import('./ApiExportInline'));
const DocumentEditorInline = React.lazy(() => import('./DocumentEditorInline'));
const CreateFileInline = React.lazy(() => import('./CreateFileInline'));
const CalculatorInline = React.lazy(() => import('./CalculatorInline'));
const ParseJSONInline = React.lazy(() => import('./ParseJSONInline'));
const BulkAssessmentInline = React.lazy(() => import('./BulkAssessmentInline'));
const ApiRequestAndResponseInline = React.lazy(() => import('./ApiRequestAndResponseInline'));
const HttpRequestInline = React.lazy(() => import('./HttpRequestInline'));

// Control Flow
const BranchPathsInline = React.lazy(() => import('./BranchPathsInline'));
const ConvergeThreadsInline = React.lazy(() => import('./ConvergeThreadsInline'));
const MergeWorkflowInline = React.lazy(() => import('./MergeWorkflowInline'));
const LoopThroughListInline = React.lazy(() => import('./LoopThroughListInline'));
const SessionVariablesInline = React.lazy(() => import('./SessionVariablesInline'));
const ConditionalLogicInline = React.lazy(() => import('./ConditionalLogicInline'));
const ConditionalWorkflowInline = React.lazy(() => import('./ConditionalWorkflowInline'));
const ConditionalWorkflowPathInline = React.lazy(() => import('./ConditionalWorkflowPathInline'));
const ListTransformInline = React.lazy(() => import('./ListTransformInline'));
const AwaitExternalEventInline = React.lazy(() => import('./AwaitExternalEventInline'));
const GatewayInline = React.lazy(() => import('./GatewayInline'));

// Triggers
const ScheduleStartInline = React.lazy(() => import('./ScheduleStartInline'));
const EmailTriggerInline = React.lazy(() => import('./EmailTriggerInline'));
const TriggerAZapInline = React.lazy(() => import('./TriggerAZapInline'));
const TriggerFromApiPollingInline = React.lazy(() => import('./TriggerFromApiPollingInline'));
const TriggerOnDatabaseUpdateInline = React.lazy(() => import('./TriggerOnDatabaseUpdateInline'));
const TriggerFromAWorkflowInline = React.lazy(() => import('./TriggerFromAWorkflowInline'));
const TriggerAnotherWorkflowInline = React.lazy(() => import('./TriggerAnotherWorkflowInline'));
const TriggerFromExternalInline = React.lazy(() => import('./TriggerFromExternalInline'));

// Database
const QueryDatabaseInline = React.lazy(() => import('./QueryDatabaseInline'));
const UpdateDatabaseRecordInline = React.lazy(() => import('./UpdateDatabaseRecordInline'));
const CreateDatabaseRecordInline = React.lazy(() => import('./CreateDatabaseRecordInline'));

// Tables V2
const QueryTableV2Inline = React.lazy(() => import('./QueryTableV2Inline'));
const CreateRowV2Inline = React.lazy(() => import('./CreateRowV2Inline'));
const UpdateRowV2Inline = React.lazy(() => import('./UpdateRowV2Inline'));
const TriggerTableV2Inline = React.lazy(() => import('./TriggerTableV2Inline'));

// AI
const AIAssistantInline = React.lazy(() => import('./AIAssistantInline'));
const AIAgentInline = React.lazy(() => import('./AIAgentInline'));
const CustomFunctionInline = React.lazy(() => import('./CustomFunctionInline'));
const FormPreviewInline = React.lazy(() => import('./FormPreviewInline'));
const DynamicFormInline = React.lazy(() => import('./DynamicFormInline'));
const DynamicReviewTaskInline = React.lazy(() => import('./DynamicReviewTaskInline'));
const FormStepInline = React.lazy(() => import('./FormStepInline'));

/**
 * Registry mapping component type → lazy-loaded inline config component.
 * Returns null for types not in this registry (triggers legacy fallback).
 *
 * Excluded (legacy fallback only):
 *   - trigger_from_zapier (dumped)
 */
const inlineConfigRegistry = {
  // ─── Core Actions ───────────────────────────────────────────────────
  time_delay: TimeDelayInline,
  email: EmailInline,
  slack: SlackInline,
  workflow_data: SlackInline,
  code: CodeInline,
  advanced_code: CodeInline,
  webhook: WebhookInline,
  webhook_trigger: WebhookTriggerInline,
  api_export: ApiExportInline,
  document_editor: DocumentEditorInline,
  create_file: CreateFileInline,
  calculator: CalculatorInline,
  parse_json: ParseJSONInline,
  bulk_assessment: BulkAssessmentInline,
  api_request_and_response: ApiRequestAndResponseInline,
  http_request: HttpRequestInline,

  // ─── Control Flow ───────────────────────────────────────────────────
  branch_paths: BranchPathsInline,
  converge_threads: ConvergeThreadsInline,
  merge_workflow: MergeWorkflowInline,
  loop_through_list: LoopThroughListInline,
  session_variables: SessionVariablesInline,
  conditional_logic: ConditionalLogicInline,
  conditional_workflow: ConditionalWorkflowInline,
  conditional_workflow_path: ConditionalWorkflowPathInline,
  list_transform: ListTransformInline,
  await_external_event: AwaitExternalEventInline,
  gateway: GatewayInline,

  // ─── Triggers ───────────────────────────────────────────────────────
  schedule_start: ScheduleStartInline,
  trigger_via_email: EmailTriggerInline,
  trigger_a_zap: TriggerAZapInline,
  trigger_from_api_polling: TriggerFromApiPollingInline,
  trigger_on_database_update: TriggerOnDatabaseUpdateInline,
  trigger_from_a_workflow: TriggerFromAWorkflowInline,
  trigger_another_workflow: TriggerAnotherWorkflowInline,
  trigger_from_external_app: TriggerFromExternalInline,

  // ─── Database ───────────────────────────────────────────────────────
  query_database_record: QueryDatabaseInline,
  data_bank: QueryDatabaseInline,
  update_database_record: UpdateDatabaseRecordInline,
  create_database_record: CreateDatabaseRecordInline,

  // ─── Tables V2 ────────────────────────────────────────────────────
  query_table_v2: QueryTableV2Inline,
  create_row_v2: CreateRowV2Inline,
  update_row_v2: UpdateRowV2Inline,
  trigger_table_v2: TriggerTableV2Inline,

  // ─── AI ─────────────────────────────────────────────────────────────
  ai_assistant: AIAssistantInline,
  ai_agent: AIAgentInline,

  // ─── Custom Functions ───────────────────────────────────────────────
  custom_function: CustomFunctionInline,

  // ─── Forms ──────────────────────────────────────────────────────────
  form: FormPreviewInline,
  sequential_form: FormPreviewInline,
  form_section: FormPreviewInline,
  dynamic_form: DynamicFormInline,
  dynamic_review_task: DynamicReviewTaskInline,
  form_step: FormStepInline,
};

/**
 * Returns the inline config component for a given component type,
 * or null if no dedicated inline config exists yet.
 */
export function getInlineConfig(componentType) {
  return inlineConfigRegistry[componentType] || null;
}

/**
 * Returns true if a dedicated inline config exists for this component type.
 */
export function hasInlineConfig(componentType) {
  return componentType in inlineConfigRegistry;
}

export default inlineConfigRegistry;
